import { useCallback, useEffect, useRef, useState } from "react";

export const useDebouncedValue = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [delay, value]);

  return debouncedValue;
};

export const useDebouncedCallback = (callback, delay = 400) => {
  const callbackRef = useRef(callback);
  const timerRef = useRef(null);
  const pendingArgsRef = useRef([]);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const cancel = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    pendingArgsRef.current = [];
  }, []);

  const run = useCallback(
    (...args) => {
      cancel();
      pendingArgsRef.current = args;
      timerRef.current = setTimeout(() => {
        const pendingArgs = pendingArgsRef.current;
        timerRef.current = null;
        pendingArgsRef.current = [];
        callbackRef.current?.(...pendingArgs);
      }, delay);
    },
    [cancel, delay],
  );

  const flush = useCallback(
    (...args) => {
      const nextArgs = args.length ? args : pendingArgsRef.current;
      cancel();
      return callbackRef.current?.(...nextArgs);
    },
    [cancel],
  );

  useEffect(() => cancel, [cancel]);

  return { run, flush, cancel };
};
